import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import Guid from "_shared/guids/guid";
import { Currency } from "_shared/money/currencies/currency";
import { FeeScheduleJSON } from "work/fee-schedules/_schemas/fee-schedule-schema";
import FeeScheduleSchema from "work/fee-schedules/_schemas/fee-schedule-schema";
import FeeSchedulesQuery from "work/fee-schedules/_api/queries/contracts/fee-schedules-query";
import z from "zod";

export type FeeScheduleListItemJSON = FeeScheduleJSON & { id?: string | null };
export type FeeScheduleLoadIssue = { id: Guid; issue: string };

const GuidApiResponseZ = z.union([z.uuid(), z.object({ value: z.uuid() })]).transform((value) => {
  return typeof value === "string" ? value : value.value;
});

const StringApiResponseZ = z.union([z.string(), z.object({ value: z.string() })]).transform((value) => {
  return typeof value === "string" ? value : value.value;
});

const CurrencyApiResponseZ = z.union([z.enum(Currency), z.object({ value: z.enum(Currency) })]).transform((value) => {
  return typeof value === "string" ? value : value.value;
});

const FeeScheduleCategoryApiResponseJSONZ = FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ.extend({
  id: GuidApiResponseZ.nullish(),
  name: StringApiResponseZ,
  description: StringApiResponseZ.nullish(),
  billingCode: StringApiResponseZ.nullish(),
  feeCurrency: CurrencyApiResponseZ.nullish(),
  feeUnit: StringApiResponseZ.nullish(),
});

const FeeScheduleApiResponseJSONZ = FeeScheduleSchema.FeeScheduleJSONZ.extend({
  workAgreementId: GuidApiResponseZ,
  categories: z.array(FeeScheduleCategoryApiResponseJSONZ).nullish(),
  clientFeeScheduleTemplateIds: z.array(GuidApiResponseZ).nullish(),
  vendorFeeScheduleTemplateIds: z.array(GuidApiResponseZ).nullish(),
});

export const feeSchedulesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getFeeSchedulesByQuery: build.query<Paginated<FeeScheduleListItemJSON>, { query: FeeSchedulesQuery; interfaceName: string }>({
      query: ({ query }) => `work/fee-schedules?${query.searchParams.toString()}`,
      rawResponseSchema: z.array(FeeScheduleApiResponseJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: FeeScheduleListItemJSON[], meta): Paginated<FeeScheduleListItemJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.data.map((item) => ({ type: "FeeSchedule" as const, id: item.workAgreementId })) ?? []),
              { type: "FeeSchedule" as const, id: "LIST" },
            ]
          : [{ type: "FeeSchedule" as const, id: "LIST" }],
    }),
  }),
});

export const { useGetFeeSchedulesByQueryQuery } = feeSchedulesApi;
